/**
 * Typed in-process event bus for the coach dashboard.
 *
 * Replaces a handful of `window.dispatchEvent(new Event("arc-*"))` +
 * `window.addEventListener("arc-*", ...)` call sites so event names
 * are compile-time checked and a misbehaving listener on `/today`
 * can't inadvertently hook a listener registered for `/inbox`.
 *
 * Deliberately tiny — no payload support, no priority queue, no
 * async. Every consumer today just wants "something happened, go
 * re-fetch your data." If we ever need more, expand this file; do
 * not grow the event surface in `window`.
 *
 * Usage:
 *   const off = coachEventBus.on("inbox-refresh", () => void reload());
 *   coachEventBus.emit("inbox-refresh");
 *   off();
 */

export type CoachEventName = "inbox-refresh";

type Listener = () => void;

class CoachEventBus {
  private readonly subscribers = new Map<CoachEventName, Set<Listener>>();

  on(name: CoachEventName, listener: Listener): () => void {
    let bucket = this.subscribers.get(name);
    if (!bucket) {
      bucket = new Set();
      this.subscribers.set(name, bucket);
    }
    bucket.add(listener);
    return () => {
      bucket?.delete(listener);
    };
  }

  emit(name: CoachEventName): void {
    const bucket = this.subscribers.get(name);
    if (!bucket) return;
    for (const listener of bucket) {
      try {
        listener();
      } catch {
        // Isolate listener failures so one buggy surface can't stop
        // the others from receiving the event.
      }
    }
  }

  /** Test helper — remove every listener. Do not call from app code. */
  _resetForTesting(): void {
    this.subscribers.clear();
  }

  /** Read-only count for a single event. Test + debug only. */
  _listenerCount(name: CoachEventName): number {
    return this.subscribers.get(name)?.size ?? 0;
  }
}

export const coachEventBus = new CoachEventBus();
